import React, { useMemo, useState, useEffect } from "react";
import { Card, Tabs, Alert, Typography } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarOutlined,
  HomeOutlined,
  SettingOutlined,
  TeamOutlined,
  BankOutlined,
  DollarCircleOutlined,
  WalletOutlined,
  ToolOutlined,
  FileTextOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { useHotelPermissions } from "./hooks/useHotelPermissions";
import PlaceholderPanel from "./components/PlaceholderPanel";
import ReservationsTab from "./reservations/ReservationsTab";
import RoomsAvailabilityTab from "./rooms/RoomsAvailabilityTab";
import RatesTab from "./rates/RatesTab";
import GuestsTab from "./guests/GuestsTab";
import AccountsTab from "./accounts/AccountsTab";
import OperationsTab from "./operations/OperationsTab";
import FoliosTab from "./folios/FoliosTab";
import ServicesTab from "./services/ServicesTab";
import PaymentsTab from "./payments/PaymentsTab";
import ConfigurationTab from "./configuration/ConfigurationTab";
import ExpensesTab from "./expenses/ExpensesTab";

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

type TabConfig = {
  key: string;
  label: React.ReactNode;
  visible: boolean;
  content: React.ReactNode;
};

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const GestionHotellerie: React.FC = () => {
  const permissions = useHotelPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();

  const tabs = useMemo<TabConfig[]>(
    () => [
      {
        key: "reservations",
        label: (
          <span>
            <CalendarOutlined /> Reservations
          </span>
        ),
        visible: permissions.canViewReservations,
        content: <ReservationsTab />,
      },
      {
        key: "rooms",
        label: (
          <span>
            <HomeOutlined /> Chambres & disponibilites
          </span>
        ),
        visible: permissions.canViewRooms,
        content: <RoomsAvailabilityTab />,
      },
      {
        key: "rates",
        label: (
          <span>
            <DollarCircleOutlined /> Tarifs
          </span>
        ),
        visible: permissions.canViewRates,
        content: <RatesTab />,
      },
      {
        key: "guests",
        label: (
          <span>
            <TeamOutlined /> Clients
          </span>
        ),
        visible: permissions.canViewGuests,
        content: <GuestsTab />,
      },
      {
        key: "accounts",
        label: (
          <span>
            <BankOutlined /> Comptes
          </span>
        ),
        visible: permissions.canViewAccounts,
        content: <AccountsTab />,
      },
      {
        key: "folios",
        label: (
          <span>
            <FileTextOutlined /> Folios
          </span>
        ),
        visible: permissions.canViewFolios,
        content: <FoliosTab />,
      },
      {
        key: "services",
        label: (
          <span>
            <AppstoreOutlined /> Services
          </span>
        ),
        visible: permissions.canViewServices,
        content: <ServicesTab />,
      },
      {
        key: "operations",
        label: (
          <span>
            <ToolOutlined /> Operations
          </span>
        ),
        visible: permissions.canViewOperations,
        content: <OperationsTab />,
      },
      {
        key: "payments",
        label: (
          <span>
            <DollarCircleOutlined /> Paiements
          </span>
        ),
        visible: permissions.canViewPayments,
        content: <PaymentsTab />,
      },
      {
        key: "expenses",
        label: (
          <span>
            <WalletOutlined /> Charges Admin
          </span>
        ),
        visible: permissions.canViewExpenses,
        content: <ExpensesTab />,
      },
      {
        key: "configuration",
        label: (
          <span>
            <SettingOutlined /> Configuration
          </span>
        ),
        visible: permissions.canViewConfiguration,
        content: <ConfigurationTab />,
      },
    ],
    [permissions]
  );

  const visibleTabs = useMemo(
    () => tabs.filter((tab) => tab.visible),
    [tabs]
  );

  const queryTab = query.get("tab");
  const defaultTab = visibleTabs[0]?.key || "reservations";
  const resolvedTab =
    queryTab && visibleTabs.some((tab) => tab.key === queryTab)
      ? queryTab
      : defaultTab;

  const [activeTab, setActiveTab] = useState(resolvedTab);

  useEffect(() => {
    setActiveTab(resolvedTab);
  }, [resolvedTab]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const params = new URLSearchParams(location.search);
    params.set("tab", key);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  if (!permissions.canViewModule) {
    return (
      <Card>
        <Alert
          message="Acces refuse"
          description="Vous n'avez pas acces au module Hotellerie. Contactez votre administrateur."
          type="warning"
          showIcon
          style={{ maxWidth: 600, margin: "40px auto" }}
        />
      </Card>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2} style={{ marginBottom: 4 }}>
          Module Hotellerie
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Pilotage complet des reservations, chambres, tarifs et operations.
        </Paragraph>
      </div>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          size="large"
          type="card"
        >
          {visibleTabs.map((tab) => (
            <TabPane tab={tab.label} key={tab.key}>
              {tab.content}
            </TabPane>
          ))}
        </Tabs>
      </Card>
    </div>
  );
};

export default GestionHotellerie;
